import { type FC, type ReactNode } from 'react';
import { type StreamForStreamDashboard } from 'Lessons';
import { type CourseCard, type ShareProvider, type StreamSummaryLink } from '../StreamDashboard.types';
import { getExamScore, hasTimeLimit, isStreamComplete, isStreamInProgress, isStreamStarted } from '../selectors';
import { BookmarkButton } from './BookmarkButton';
import { CourseTalkReviewWidget } from './CourseTalkReviewWidget';
import { ExamScheduleBox } from './ExamScheduleBox';
import { ExamScoreBox } from './ExamScoreBox';
import { ResourceLinksBox } from './ResourceLinksBox';
import { ShareStreamSidebar } from './ShareStreamSidebar';
import { TimerBox } from './TimerBox';
import { WhatYouWillLearnBox } from './WhatYouWillLearnBox';
import { CourseInformationCaption } from './CourseInformationCaption';
import { CourseInformationGroup } from './CourseInformationGroup';

/*
    The right column. Resources sit above Course Highlights when the course is complete, below otherwise.
    The mobile collapse clips to zero rather than unmounting. A complete course's resources stay visible on purpose.
*/

type Props = {
    stream: StreamForStreamDashboard;
    isSignedIn: boolean;
    canShare: boolean;
    favorite: boolean;
    showBookmark: boolean;
    onToggleBookmark: () => void;
    onShare: (provider: ShareProvider) => void;
    formattedExamOpenTime: string | null;
    timeLimitHours: number | null;
    showSchedule: boolean;
    onShowSchedule: () => void;
    recommendedCourses: CourseCard[];
    relatedCourses: CourseCard[];
    onOpenCourse: (streamId: string) => void;
    summaries: StreamSummaryLink[];
    resourcesDisabled: boolean;
    onSummaryClick: (summaryId: string) => void;
    onResourceLinkClick: (url: string) => void;
    keyTermsReady: boolean;
    hasKeyTerms: boolean;
    onExploreKeyTerms: () => void;
    courseTalkId: number | null;
    certificate: ReactNode;
};

/*
    Everything main puts inside `.course-information-box`: the resources box, Course Highlights, and the
    recommended, related and credits boxes. The caption heads that container, so it renders when any of
    them will.
*/
function hasCourseInformation({
    stream,
    hasResources,
    recommendedCourses,
    relatedCourses,
}: {
    stream: StreamForStreamDashboard;
    hasResources: boolean;
    recommendedCourses: CourseCard[];
    relatedCourses: CourseCard[];
}) {
    return (
        (stream.whatYouWillLearn?.length ?? 0) > 0 ||
        hasResources ||
        recommendedCourses.length > 0 ||
        relatedCourses.length > 0 ||
        (stream.credits?.length ?? 0) > 0
    );
}

/*
    The timer, the score and the schedule. Extracted from StreamDashboardSidebar to keep that function
    inside the 100-line lint cap; the three boxes have no behaviour of their own.
*/
const SidebarExamBoxes: FC<{
    stream: StreamForStreamDashboard;
    showExamBoxes: boolean;
    timeLimitHours: number | null;
    examScore: string | null;
    formattedExamOpenTime: string | null;
    showSchedule: boolean;
    onShowSchedule: () => void;
}> = ({ stream, showExamBoxes, timeLimitHours, examScore, formattedExamOpenTime, showSchedule, onShowSchedule }) => (
    <>
        {showExamBoxes && (
            <TimerBox
                timeRunsOutAt={stream.lessonStreamsProgress?.timeRunsOutAt ?? null}
                timeLimitHours={timeLimitHours}
                hasStarted={isStreamStarted(stream)}
                isInProgress={isStreamInProgress(stream)}
                className="mb-3 max-md:hidden sm:mb-7.5"
            />
        )}

        <ExamScoreBox examScore={examScore} className="mb-3 sm:mb-7.5" />

        <ExamScheduleBox
            formattedExamOpenTime={formattedExamOpenTime}
            timeLimitHours={timeLimitHours}
            showSchedule={showSchedule}
            onShowSchedule={onShowSchedule}
            className="mb-3 max-md:hidden sm:mb-7.5"
        />
    </>
);

export const StreamDashboardSidebar: FC<Props> = ({
    stream,
    isSignedIn,
    canShare,
    favorite,
    showBookmark,
    onToggleBookmark,
    onShare,
    formattedExamOpenTime,
    timeLimitHours,
    showSchedule,
    onShowSchedule,
    recommendedCourses,
    relatedCourses,
    onOpenCourse,
    summaries,
    resourcesDisabled,
    onSummaryClick,
    onResourceLinkClick,
    keyTermsReady,
    hasKeyTerms,
    onExploreKeyTerms,
    courseTalkId,
    certificate,
}) => {
    const examScore = getExamScore(stream);
    const showExamBoxes = hasTimeLimit(stream) && !isStreamComplete(stream);

    // The box's own guard, plus a user check: downloads and links come off the stream and are not user-gated.
    const hasResources =
        isSignedIn && (summaries.length > 0 || stream.resourceDownloads.length > 0 || stream.resourceLinks.length > 0);

    const resources = (
        <ResourceLinksBox
            summaries={summaries}
            resourceDownloads={stream.resourceDownloads}
            resourceLinks={stream.resourceLinks}
            disabled={resourcesDisabled}
            onSummaryClick={onSummaryClick}
            onLinkClick={onResourceLinkClick}
            className="mb-3 sm:mb-7.5"
        />
    );

    return (
        <div className="w-full md:w-1/3">
            <div className="max-md:hidden">{certificate}</div>

            {canShare && !stream.exam && (
                <ShareStreamSidebar
                    onShare={onShare}
                    variant="row"
                    className="mb-3 max-md:hidden md:mb-6.25 lg:hidden"
                />
            )}

            {showBookmark && (
                <BookmarkButton
                    favorite={favorite}
                    disabled={false}
                    onToggle={onToggleBookmark}
                    className="mb-3 max-md:hidden sm:mb-7.5"
                />
            )}

            <SidebarExamBoxes
                stream={stream}
                showExamBoxes={showExamBoxes}
                timeLimitHours={timeLimitHours}
                examScore={examScore}
                formattedExamOpenTime={formattedExamOpenTime}
                showSchedule={showSchedule}
                onShowSchedule={onShowSchedule}
            />

            {hasCourseInformation({ stream, hasResources, recommendedCourses, relatedCourses }) && (
                <CourseInformationCaption className="mt-7.5" />
            )}

            {isStreamComplete(stream) && hasResources && resources}

            <WhatYouWillLearnBox
                topics={stream.whatYouWillLearn ?? []}
                hasKeyTerms={hasKeyTerms}
                keyTermsReady={keyTermsReady}
                onExploreKeyTerms={onExploreKeyTerms}
                className="mb-3 sm:mb-7.5"
            />

            <CourseInformationGroup
                streamTitle={stream.title}
                credits={stream.credits}
                recommendedCourses={recommendedCourses}
                relatedCourses={relatedCourses}
                onOpenCourse={onOpenCourse}
                resources={!isStreamComplete(stream) && hasResources ? resources : null}
            />

            {/* No user check: useCourseTalk already reproduces the four conditions the old directive
                had, and its hasExternalInstitution gate reads `currentUser && ...`, so a visitor
                passed it deliberately. These are public course pages. */}
            <CourseTalkReviewWidget courseTalkId={courseTalkId} className="mb-3 sm:mb-7.5" />
        </div>
    );
};
